import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useSearchQuery } from '@/domain/search/contexts';
import { useJobRecommendationsLazyQuery } from '@/graphql/generated';
import { getFilteredRecommendations } from '@/helpers/get-filtered-recommendations';
import { useLoginStates } from '@/hooks';

import { DS, ITEMS_PER_PAGE } from '../../../constants';
import { isVisibleJob } from '../../../helpers';
import { useSearch } from '../../../hooks/use-search';
import { LimitedJobCount } from '../../limited-job-count';
import { ResultsHeader } from '../header';
import { ResultsList } from '../list';
import { ResultsSkeleton } from '../skeleton';
import { EmptyBanner } from './banner';

import * as S from './empty-state.styles';

type Props = {
  hasError?: boolean;
};

export const EmptyState: React.VFC<Props> = ({ hasError = false }) => {
  const { isLoggedIn } = useLoginStates();
  const [hasRecommendations, setHasRecommendations] = useState(false);
  const {
    searchQuery: { keywords, location },
  } = useSearchQuery();
  const hasNoKeywordsNoLocation = !keywords && !location;

  const { data: searchResultsData, loading: searchResultsLoading } = useSearch({ isEmptyStateSearch: true });
  const searchResults = searchResultsData?.jobSearchByQuery?.collection ?? [];
  const filteredSearchResults = searchResults.filter(isVisibleJob);
  const searchResultsTotal = searchResultsData?.jobSearchByQuery?.total ?? 0;

  const [getRecommendations, { data: recommendationsData, loading: recommendationsLoading }] =
    useJobRecommendationsLazyQuery({
      variables: {
        consumer: DS.CONSUMER.LI,
        first: ITEMS_PER_PAGE,
      },
      errorPolicy: 'all',
    });
  const recommendations = recommendationsData?.viewer?.jobRecommendations?.edges ?? [];
  const filteredRecommendations = getFilteredRecommendations(recommendations);

  useEffect(() => {
    if (isLoggedIn && !searchResultsLoading && filteredSearchResults.length === 0) {
      getRecommendations();
      setHasRecommendations(true);
    }
  }, [filteredSearchResults.length, getRecommendations, isLoggedIn, searchResultsLoading]);

  return (
    <>
      {hasError ? (
        <S.ErrorBanner
          {...(hasNoKeywordsNoLocation && {
            headlineCopy: 'JOBS_SEARCH_EMPTY_ERROR_HEADLINE',
            textCopy: 'JOBS_SEARCH_EMPTY_ERROR_TEXT',
          })}
        />
      ) : (
        <>
          <ResultsHeader hideSorting />
          <EmptyBanner />
        </>
      )}
      {searchResultsLoading || recommendationsLoading ? (
        <ResultsSkeleton hideButtons />
      ) : filteredSearchResults.length > 0 || filteredRecommendations.length > 0 ? (
        <>
          <S.Headline size="large" sizeWide="xxlarge">
            <FormattedMessage
              id={
                hasRecommendations
                  ? 'JOBS_SEARCH_EMPTY_STATE_RECOMMENDATIONS_HEADLINE'
                  : searchResultsTotal > 1
                  ? 'JOBS_SEARCH_LOCATION_OFF_RESULTS_TITLE_PLURAL'
                  : 'JOBS_SEARCH_LOCATION_OFF_RESULTS_TITLE'
              }
              values={{ jobs_found: <LimitedJobCount count={searchResultsTotal} /> }}
            />
          </S.Headline>
          <ResultsList
            items={hasRecommendations ? filteredRecommendations : filteredSearchResults}
            type={hasRecommendations ? 'EMPTY_STATE_RECOS' : 'EMPTY_STATE_SEARCH'}
            isSearchFallback
          />
        </>
      ) : null}
    </>
  );
};
